import React, { useEffect, useRef, useState } from "react";
import "./PortfolioSimulator.css";

const ACCESS_STORAGE_KEY = "cartera-rsl-access";
// TODO: replace with your real Substack publication URL.
const SUBSCRIBE_URL = "https://rslcartera.substack.com/subscribe";

function verifyPassword(password) {
  return fetch("/api/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password: password }),
  })
    .then(function (r) { return r.json(); })
    .then(function (data) { return !!(data && data.ok); })
    .catch(function () { return false; });
}

// Static markup for the simulator shell. The interactive behaviour below
// (state, simulation math, chart rendering) manipulates this DOM directly
// via document.getElementById, the same way it runs as a plain page --
// wrapped in a React component so it lives in this app's build/deploy
// pipeline instead of a separate static file.
const MARKUP = `<div class="app">
  <header class="topbar">
    <div class="brand">
      <svg class="brand-mark" width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect width="38" height="38" rx="10" fill="var(--accent-wash)"/>
        <path d="M8 25 L14 17 L19 21 L26 11 L31 15" stroke="var(--accent)" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
        <circle cx="31" cy="15" r="2.4" fill="var(--accent)"/>
      </svg>
      <div>
        <h1 class="brand-name">Cartera RSL Simulator</h1>
        <p class="brand-tag">Simulador de cartera mixta &mdash; renta variable, bonos y cripto</p>
      </div>
    </div>
    <div class="topbar-actions">
      <button class="icon-btn" id="themeToggle" type="button" aria-label="Cambiar tema claro / oscuro" title="Cambiar tema">
        <svg id="themeIconSun" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="4.2"/><path d="M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.5 1.5M17.9 17.9l1.5 1.5M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.5-1.5M17.9 6.1l1.5-1.5"/></svg>
        <svg id="themeIconMoon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none"><path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a6.8 6.8 0 0 0 10.5 10.5Z"/></svg>
      </button>
    </div>
  </header>

  <main class="layout">
    <aside class="panel" aria-label="Parametros de la simulacion">
      <section class="control-group">
        <h2>Aportaciones y horizonte</h2>
        <div class="field">
          <label for="capitalInicial">Capital inicial</label>
          <div class="unit-input"><input type="number" id="capitalInicial" min="0" step="100"><span>USD</span></div>
        </div>
        <div class="field">
          <label for="aportacionMensual">Aportacion mensual</label>
          <div class="unit-input"><input type="number" id="aportacionMensual" min="0" step="10"><span>USD</span></div>
        </div>
        <div class="field">
          <div class="field-row"><label for="horizonte">Horizonte</label><strong id="horizonteVal" style="font:600 13px var(--font-mono)"></strong></div>
          <input type="range" id="horizonte" min="1" max="40" step="1">
        </div>
        <label class="checkbox-row"><input type="checkbox" id="inflacionToggle"> Mostrar en valor real (ajustado por inflacion)</label>
        <div class="field" id="inflacionRow">
          <label for="inflacionPct">Inflacion anual esperada</label>
          <div class="unit-input"><input type="number" id="inflacionPct" min="0" max="20" step="0.1"><span>%</span></div>
        </div>
      </section>

      <section class="control-group">
        <div class="control-group-head">
          <h2>Composicion de la cartera</h2>
          <span class="weight-total" id="weightTotal">100,0%</span>
        </div>
        <div id="assetControls"></div>
        <div class="control-buttons">
          <button class="btn" id="normalizeBtn" type="button">Normalizar a 100%</button>
          <button class="btn" id="resetBtn" type="button">Restablecer</button>
        </div>
      </section>

      <p class="disclaimer">Simulador educativo. Las rentabilidades y volatilidades son supuestos que tu introduces, no predicciones ni asesoramiento de inversion. No incluye comisiones, impuestos ni correlacion real entre activos. Rentabilidades pasadas no garantizan resultados futuros.</p>
    </aside>

    <section class="workbench">
      <div class="kpi-row" id="kpiRow"></div>

      <div class="chart-row">
        <div class="card chart-card">
          <div class="card-head">
            <h2>Proyeccion de valor</h2>
            <div class="legend-line">
              <span class="legend-item"><span class="legend-key" style="background:var(--accent)"></span>Escenario base</span>
              <span class="legend-item"><span class="legend-key" style="background:var(--text-muted)"></span>Rango &plusmn;1&sigma; anual</span>
            </div>
          </div>
          <div class="chart-wrap">
            <svg id="lineChart" viewBox="0 0 760 340" role="img" aria-label="Proyeccion del valor de la cartera en el tiempo"></svg>
            <div class="tooltip" id="lineTooltip" hidden></div>
          </div>
        </div>

        <div class="card donut-card">
          <div class="card-head"><h2>Distribucion de la cartera</h2></div>
          <div class="donut-body">
            <div class="donut-wrap">
              <svg id="donutChart" viewBox="0 0 220 220" role="img" aria-label="Distribucion de la cartera por activo"></svg>
              <div class="donut-center">
                <span class="l">Valor inicial</span>
                <span class="v" id="donutCenterValue"></span>
              </div>
              <div class="tooltip" id="donutTooltip" hidden></div>
            </div>
            <ul class="donut-legend" id="donutLegend"></ul>
          </div>
        </div>
      </div>

      <div class="card table-card">
        <div class="card-head">
          <h2>Proyeccion por activo</h2>
          <span class="card-note" id="tableHorizonNote"></span>
        </div>
        <div class="table-scroll">
          <table>
            <thead>
              <tr><th>Activo</th><th>Peso</th><th>Rent. esperada</th><th>Valor inicial</th><th>Valor proyectado</th><th>% cartera final</th></tr>
            </thead>
            <tbody id="assetTableBody"></tbody>
          </table>
        </div>
      </div>

      <p class="foot-note"><strong>Como se calcula:</strong> cada activo se proyecta por separado, capitalizando mensualmente su propia rentabilidad esperada sobre el capital inicial asignado y las aportaciones mensuales asignadas; el total es la suma de los nueve activos, igual que en la tabla. El rango sombreado del grafico es la volatilidad anual de la cartera (la volatilidad de cada activo, ponderada por su peso, combinada asumiendo independencia entre activos &mdash; una simplificacion que no captura su correlacion real) aplicada como una banda de incertidumbre que crece con la raiz cuadrada del tiempo alrededor del escenario base. La T.A.E. es la tasa interna de retorno anualizada de todos los flujos (capital inicial, aportaciones y valor final).</p>
    </section>
  </main>
</div>`;

export default function PortfolioSimulator() {
  const containerRef = useRef(null);
  const initializedRef = useRef(false);
  const [checking, setChecking] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // On load, silently re-check any previously-entered password. This keeps a
  // returning visitor unlocked without retyping it, but still re-locks them
  // automatically once the shared password is rotated (the stored value will
  // no longer verify).
  useEffect(() => {
    let cancelled = false;
    var stored = null;
    try { stored = localStorage.getItem(ACCESS_STORAGE_KEY); } catch (e) {}
    if (!stored) { setChecking(false); return undefined; }
    verifyPassword(stored).then(function (ok) {
      if (cancelled) return;
      if (ok) {
        setUnlocked(true);
      } else {
        try { localStorage.removeItem(ACCESS_STORAGE_KEY); } catch (e) {}
      }
      setChecking(false);
    });
    return function () { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!unlocked) return;
    if (initializedRef.current) return;
    initializedRef.current = true;
    initApp();
  }, [unlocked]);

  function handleSubmit(e) {
    e.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    setError("");
    verifyPassword(password).then(function (ok) {
      setSubmitting(false);
      if (ok) {
        try { localStorage.setItem(ACCESS_STORAGE_KEY, password); } catch (e) {}
        setUnlocked(true);
      } else {
        setError("Contraseña incorrecta. Revisa el correo de bienvenida de tu suscripción.");
      }
    });
  }

  if (checking) {
    return <div className="access-gate" aria-hidden="true" />;
  }

  if (!unlocked) {
    return (
      <div className="access-gate">
        <form className="access-card" onSubmit={handleSubmit}>
          <span className="access-eyebrow">Acceso para suscriptores</span>
          <h1 className="access-title">Cartera RSL Simulator</h1>
          <p className="access-copy">
            Esta herramienta es exclusiva para suscriptores de pago. Introduce la
            contraseña que recibiste en tu correo de bienvenida.
          </p>
          <input
            type="password"
            className="access-input"
            placeholder="Contraseña de acceso"
            value={password}
            onChange={function (e) { setPassword(e.target.value); }}
            autoFocus
          />
          {error ? <p className="access-error">{error}</p> : null}
          <button type="submit" className="access-submit" disabled={submitting}>
            {submitting ? "Comprobando…" : "Entrar"}
          </button>
          <a className="access-subscribe" href={SUBSCRIBE_URL} target="_blank" rel="noreferrer">
            ¿Aún no eres suscriptor? Suscríbete aquí
          </a>
        </form>
      </div>
    );
  }

  return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: MARKUP }} />;
}

function initApp() {
  var STORAGE_KEY = "faro-patrimonial-state-v1";
  var THEME_KEY = "faro-patrimonial-theme";

  var DEFAULT_ASSETS = [
    { id:"vus500", name:"Vanguard U.S. 500",        cat:"trad",   colorVar:"--s-blue",    weight:35.0, ret:8.0,  vol:15 },
    { id:"vgs",    name:"Vanguard Global Stock",     cat:"trad",   colorVar:"--s-aqua",    weight:24.0, ret:7.0,  vol:14 },
    { id:"ves",    name:"Vanguard European Stock",   cat:"trad",   colorVar:"--s-yellow",  weight:8.9,  ret:6.0,  vol:16 },
    { id:"egb",    name:"Euro Gov Bond",             cat:"trad",   colorVar:"--s-orange",  weight:7.0,  ret:2.5,  vol:5  },
    { id:"eib",    name:"Euro Inv Grade Bond",       cat:"trad",   colorVar:"--s-red",     weight:7.5,  ret:3.0,  vol:6  },
    { id:"gbh",    name:"Global Bond Hedged",        cat:"trad",   colorVar:"--s-violet",  weight:7.4,  ret:3.2,  vol:5  },
    { id:"btc",    name:"Bitcoin",                   cat:"cripto", colorVar:"--s-magenta", weight:4.9,  ret:15.0, vol:55 },
    { id:"eth",    name:"Ethereum",                  cat:"cripto", colorVar:"--s-green",   weight:3.8,  ret:14.0, vol:60 },
    { id:"xrp",    name:"XRP",                       cat:"cripto", colorVar:"--s-tan",     weight:1.5,  ret:8.0,  vol:65 }
  ];

  var DEFAULT_STATE = {
    capitalInicial: 20000,
    aportacionMensual: 200,
    horizonte: 10,
    inflacionOn: false,
    inflacionPct: 2.5,
    assets: DEFAULT_ASSETS.map(function(a){ return { id:a.id, weight:a.weight, ret:a.ret }; })
  };

  var state = loadState();

  // ---------- formatters ----------
  // Manual, locale-independent formatting ($ prefix, comma thousands, period decimal).
  // Intl.NumberFormat("es-ES", ...) was tried first but its ICU data only groups
  // thousands once the leading digit-group has >=2 digits (6080 -> "6080" while
  // 27721 -> "27.721"), which reads as a bug. Formatting it by hand avoids that.
  function groupThousands(intStr){ return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ","); }
  function fmtUSD(n, decimals){
    decimals = decimals === undefined ? 2 : decimals;
    var neg = n < 0;
    var fixed = Math.abs(n).toFixed(decimals);
    var dot = fixed.indexOf(".");
    var intPart = dot === -1 ? fixed : fixed.slice(0, dot);
    var decPart = dot === -1 ? "" : fixed.slice(dot + 1);
    return (neg ? "-" : "") + "$" + groupThousands(intPart) + (decPart ? "." + decPart : "");
  }
  function fmtUSD0(n){ return fmtUSD(n, 0); }
  function fmtCompact(n){
    var sign = n < 0 ? "-" : "";
    n = Math.abs(n);
    function trim(x){ return x % 1 === 0 ? x.toFixed(0) : x.toFixed(1); }
    if (n >= 1e6) return sign + "$" + trim(n/1e6) + "M";
    if (n >= 1e3) return sign + "$" + trim(n/1e3) + "k";
    return sign + "$" + n.toFixed(0);
  }
  function fmtPct(n, d){
    d = d === undefined ? 1 : d;
    return (n > 0 ? "+" : "") + n.toFixed(d) + "%";
  }
  function fmtPctPlain(n, d){
    d = d === undefined ? 1 : d;
    return n.toFixed(d) + "%";
  }

  // ---------- persistence ----------
  function loadState(){
    try{
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return clone(DEFAULT_STATE);
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.assets) || parsed.assets.length !== DEFAULT_ASSETS.length) return clone(DEFAULT_STATE);
      return parsed;
    }catch(e){ return clone(DEFAULT_STATE); }
  }
  function saveState(){
    try{ localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }catch(e){}
  }
  function clone(o){ return JSON.parse(JSON.stringify(o)); }

  // ---------- theme ----------
  function initTheme(){
    var saved = null;
    try{ saved = localStorage.getItem(THEME_KEY); }catch(e){}
    if (saved === "light" || saved === "dark") applyTheme(saved, false);
    updateThemeIcon();
  }
  function applyTheme(mode, persist){
    if (mode) document.documentElement.setAttribute("data-theme", mode);
    else document.documentElement.removeAttribute("data-theme");
    if (persist){ try{ localStorage.setItem(THEME_KEY, mode || ""); }catch(e){} }
  }
  function updateThemeIcon(){
    var isDark = document.documentElement.getAttribute("data-theme") === "dark" ||
      (!document.documentElement.getAttribute("data-theme") && window.matchMedia("(prefers-color-scheme: dark)").matches);
    document.getElementById("themeIconSun").style.display = isDark ? "block" : "none";
    document.getElementById("themeIconMoon").style.display = isDark ? "none" : "block";
  }
  document.getElementById("themeToggle").addEventListener("click", function(){
    var current = document.documentElement.getAttribute("data-theme");
    var isDark = current === "dark" || (!current && window.matchMedia("(prefers-color-scheme: dark)").matches);
    applyTheme(isDark ? "light" : "dark", true);
    updateThemeIcon();
  });
  initTheme();

  // ---------- helpers ----------
  function svgEl(tag, attrs){
    var el = document.createElementNS("http://www.w3.org/2000/svg", tag);
    for (var k in attrs){ el.setAttribute(k, attrs[k]); }
    return el;
  }
  function normalizedAssets(){
    var sum = state.assets.reduce(function(s,a){ return s + Number(a.weight || 0); }, 0);
    var factor = sum > 0 ? 100 / sum : 0;
    return state.assets.map(function(a, i){
      var def = DEFAULT_ASSETS[i];
      return {
        id: a.id, name: def.name, cat: def.cat, colorVar: def.colorVar,
        weightRaw: Number(a.weight || 0),
        weight: Number(a.weight || 0) * factor,
        ret: Number(a.ret || 0),
        vol: def.vol
      };
    });
  }
  function weightSum(){ return state.assets.reduce(function(s,a){ return s + Number(a.weight || 0); }, 0); }

  // ---------- IRR (monthly) ----------
  function solveIRRmonthly(cashflows){
    function npv(r){
      var total = 0;
      for (var t = 0; t < cashflows.length; t++){ total += cashflows[t] / Math.pow(1 + r, t); }
      return total;
    }
    var lo = -0.99, hi = 50;
    var flo = npv(lo), fhi = npv(hi);
    if (!isFinite(flo) || !isFinite(fhi) || flo * fhi > 0) return null;
    var mid = 0;
    for (var i = 0; i < 100; i++){
      mid = (lo + hi) / 2;
      var fm = npv(mid);
      if (Math.abs(fm) < 1e-6) break;
      if ((flo < 0) === (fm < 0)) { lo = mid; flo = fm; } else { hi = mid; }
    }
    return mid;
  }

  // ---------- simulation core ----------
  function simulateAsset(initCapital, monthlyContrib, annualRate, months){
    var monthlyRate = Math.pow(1 + annualRate, 1/12) - 1;
    var series = [initCapital];
    var balance = initCapital;
    for (var m = 1; m <= months; m++){
      balance = balance * (1 + monthlyRate) + monthlyContrib;
      if (m % 12 === 0) series.push(balance);
    }
    return series; // length = years+1, index by year
  }

  function runSimulation(){
    var assets = normalizedAssets();
    var years = Math.max(1, Math.round(state.horizonte));
    var months = years * 12;
    var capital = Math.max(0, Number(state.capitalInicial) || 0);
    var contrib = Math.max(0, Number(state.aportacionMensual) || 0);

    var totalBase = new Array(years + 1).fill(0);
    var perAsset = [];
    var volVarianceSum = 0; // portfolio annual variance, assuming independence across assets

    assets.forEach(function(a){
      var w = a.weight / 100;
      var initA = capital * w;
      var contribA = contrib * w;
      var rBase = a.ret / 100;

      var sBase = simulateAsset(initA, contribA, rBase, months);
      for (var y = 0; y <= years; y++){ totalBase[y] += sBase[y]; }
      perAsset.push({ asset:a, initA:initA, contribA:contribA, finalBase: sBase[years] });

      var weightedVol = w * (a.vol / 100);
      volVarianceSum += weightedVol * weightedVol;
    });

    // Portfolio-level annual volatility (sigma), from each asset's own vol
    // weighted by its share of the portfolio. Uncertainty then widens with
    // sqrt(years) around the base path -- a standard, diversification-aware
    // approximation of a growing confidence band (not a fixed alternate
    // compounding rate held constant for the whole horizon, which would
    // wildly overstate multi-year upside/downside for volatile assets).
    var sigmaPortfolio = Math.sqrt(volVarianceSum);
    var totalOpt = totalBase.map(function(v, y){ return v * Math.exp(sigmaPortfolio * Math.sqrt(y)); });
    var totalPes = totalBase.map(function(v, y){ return v * Math.exp(-sigmaPortfolio * Math.sqrt(y)); });

    var aportadoTotal = capital + contrib * months;

    var cashflows = new Array(months + 1).fill(0);
    cashflows[0] = -capital;
    for (var m = 1; m <= months; m++){ cashflows[m] += -contrib; }
    cashflows[months] += totalBase[years];
    var irrMonthly = solveIRRmonthly(cashflows);
    var tae = irrMonthly === null ? null : Math.pow(1 + irrMonthly, 12) - 1;

    var inflFactor = function(y){
      if (!state.inflacionOn) return 1;
      return Math.pow(1 + (Number(state.inflacionPct) || 0) / 100, y);
    };

    var deflate = function(arr){ return arr.map(function(v,y){ return v / inflFactor(y); }); };

    return {
      years: years,
      yearsAxis: totalBase.map(function(_,y){ return y; }),
      base: deflate(totalBase),
      opt: deflate(totalOpt),
      pes: deflate(totalPes),
      sigmaPortfolio: sigmaPortfolio,
      perAsset: perAsset.map(function(p){
        return {
          asset: p.asset, initA: p.initA / inflFactor(0), contribA: p.contribA,
          finalBase: p.finalBase / inflFactor(years)
        };
      }),
      capital: capital / inflFactor(0),
      aportadoTotal: aportadoTotal / inflFactor(years),
      valorFinal: totalBase[years] / inflFactor(years),
      tae: tae
    };
  }

  // ---------- rendering: controls ----------
  var assetControlsEl = document.getElementById("assetControls");
  function renderAssetControls(){
    assetControlsEl.innerHTML = "";
    state.assets.forEach(function(a, idx){
      var def = DEFAULT_ASSETS[idx];
      var row = document.createElement("div");
      row.className = "asset-row";

      var dot = document.createElement("span");
      dot.className = "asset-dot";
      dot.style.background = "var(" + def.colorVar + ")";
      row.appendChild(dot);

      var body = document.createElement("div");

      var nameLine = document.createElement("div");
      nameLine.className = "asset-name-line";
      var nm = document.createElement("span");
      nm.className = "asset-name";
      nm.textContent = def.name;
      var chip = document.createElement("span");
      chip.className = "chip" + (def.cat === "cripto" ? " cripto" : "");
      chip.textContent = def.cat === "cripto" ? "Cripto" : "Tradicional";
      nameLine.appendChild(nm);
      nameLine.appendChild(chip);
      body.appendChild(nameLine);

      var inputsRow = document.createElement("div");
      inputsRow.className = "asset-inputs";

      var weightWrap = document.createElement("div");
      var weightLabel = document.createElement("span");
      weightLabel.className = "mini-label";
      weightLabel.textContent = "Peso %";
      var weightInput = document.createElement("input");
      weightInput.type = "number"; weightInput.min = "0"; weightInput.max = "100"; weightInput.step = "0.1";
      weightInput.value = a.weight;
      weightInput.setAttribute("aria-label", "Peso de " + def.name + " en porcentaje");
      weightWrap.appendChild(weightLabel); weightWrap.appendChild(weightInput);

      var retWrap = document.createElement("div");
      var retLabel = document.createElement("span");
      retLabel.className = "mini-label";
      retLabel.textContent = "Rent. %/a";
      var retInput = document.createElement("input");
      retInput.type = "number"; retInput.step = "0.1";
      retInput.value = a.ret;
      retInput.setAttribute("aria-label", "Rentabilidad anual esperada de " + def.name + " en porcentaje");
      retWrap.appendChild(retLabel); retWrap.appendChild(retInput);

      inputsRow.appendChild(weightWrap);
      inputsRow.appendChild(retWrap);
      body.appendChild(inputsRow);
      row.appendChild(body);
      assetControlsEl.appendChild(row);

      weightInput.addEventListener("input", function(){
        state.assets[idx].weight = parseFloat(weightInput.value) || 0;
        onStateChange();
      });
      retInput.addEventListener("input", function(){
        state.assets[idx].ret = parseFloat(retInput.value) || 0;
        onStateChange();
      });
    });
  }

  var capitalInicialEl = document.getElementById("capitalInicial");
  var aportacionMensualEl = document.getElementById("aportacionMensual");
  var horizonteEl = document.getElementById("horizonte");
  var horizonteValEl = document.getElementById("horizonteVal");
  var inflacionToggleEl = document.getElementById("inflacionToggle");
  var inflacionPctEl = document.getElementById("inflacionPct");
  var inflacionRowEl = document.getElementById("inflacionRow");
  var weightTotalEl = document.getElementById("weightTotal");

  function renderTopControls(){
    capitalInicialEl.value = state.capitalInicial;
    aportacionMensualEl.value = state.aportacionMensual;
    horizonteEl.value = state.horizonte;
    horizonteValEl.textContent = state.horizonte + (state.horizonte === 1 ? " ano" : " anos");
    inflacionToggleEl.checked = state.inflacionOn;
    inflacionPctEl.value = state.inflacionPct;
    inflacionRowEl.style.opacity = state.inflacionOn ? "1" : ".45";
    inflacionPctEl.disabled = !state.inflacionOn;
  }

  capitalInicialEl.addEventListener("input", function(){ state.capitalInicial = parseFloat(capitalInicialEl.value) || 0; onStateChange(); });
  aportacionMensualEl.addEventListener("input", function(){ state.aportacionMensual = parseFloat(aportacionMensualEl.value) || 0; onStateChange(); });
  horizonteEl.addEventListener("input", function(){
    state.horizonte = parseInt(horizonteEl.value, 10) || 1;
    horizonteValEl.textContent = state.horizonte + (state.horizonte === 1 ? " ano" : " anos");
    onStateChange();
  });
  inflacionToggleEl.addEventListener("change", function(){ state.inflacionOn = inflacionToggleEl.checked; onStateChange(); });
  inflacionPctEl.addEventListener("input", function(){ state.inflacionPct = parseFloat(inflacionPctEl.value) || 0; onStateChange(); });

  document.getElementById("normalizeBtn").addEventListener("click", function(){
    var sum = weightSum();
    if (sum <= 0) return;
    state.assets.forEach(function(a){ a.weight = Math.round((a.weight / sum) * 1000) / 10; });
    renderAssetControls();
    onStateChange();
  });
  document.getElementById("resetBtn").addEventListener("click", function(){
    state = clone(DEFAULT_STATE);
    renderTopControls();
    renderAssetControls();
    onStateChange();
  });

  // ---------- rendering: KPI ----------
  var kpiRowEl = document.getElementById("kpiRow");
  function kpiTile(label, value, opts){
    opts = opts || {};
    var div = document.createElement("div");
    div.className = "kpi-tile" + (opts.hero ? " hero" : "");
    var l = document.createElement("span"); l.className = "kpi-label"; l.textContent = label;
    var v = document.createElement("span"); v.className = "kpi-value"; v.textContent = value;
    div.appendChild(l); div.appendChild(v);
    if (opts.delta !== undefined && opts.delta !== null){
      var d = document.createElement("span");
      d.className = "kpi-delta " + (opts.delta >= 0 ? "pos" : "neg");
      d.textContent = fmtPct(opts.delta, 1);
      div.appendChild(d);
    }
    if (opts.sub){
      var s = document.createElement("span"); s.className = "kpi-sub"; s.textContent = opts.sub;
      div.appendChild(s);
    }
    return div;
  }
  function renderKPIs(sim){
    kpiRowEl.innerHTML = "";
    var ganancia = sim.valorFinal - sim.aportadoTotal;
    var rentTotalPct = sim.aportadoTotal > 0 ? (ganancia / sim.aportadoTotal) * 100 : 0;
    var taePct = sim.tae === null ? null : sim.tae * 100;

    kpiRowEl.appendChild(kpiTile("Valor proyectado (" + sim.years + (sim.years===1?" ano":" anos") + ")", fmtUSD(sim.valorFinal), { hero:true, delta: rentTotalPct, sub: state.inflacionOn ? "En valor real (hoy)" : "En valor nominal" }));
    kpiRowEl.appendChild(kpiTile("Aportado total", fmtUSD(sim.aportadoTotal)));
    var gTile = kpiTile("Ganancia estimada", (ganancia>=0?"+":"") + fmtUSD(ganancia));
    gTile.querySelector(".kpi-value").style.color = ganancia >= 0 ? "var(--positive)" : "var(--negative)";
    kpiRowEl.appendChild(gTile);
    var rTile = kpiTile("Rentabilidad total", fmtPct(rentTotalPct, 1));
    rTile.querySelector(".kpi-value").style.color = rentTotalPct >= 0 ? "var(--positive)" : "var(--negative)";
    kpiRowEl.appendChild(rTile);
    var taeTile = kpiTile("T.A.E. (anualizada)", taePct === null ? "n/d" : fmtPct(taePct, 2));
    if (taePct !== null) taeTile.querySelector(".kpi-value").style.color = taePct >= 0 ? "var(--positive)" : "var(--negative)";
    kpiRowEl.appendChild(taeTile);
  }

  // ---------- rendering: line chart ----------
  var lineSvg = document.getElementById("lineChart");
  var lineTooltip = document.getElementById("lineTooltip");

  function niceCeil(x){
    if (x <= 0) return 1;
    var exp = Math.floor(Math.log(x) / Math.LN10);
    var f = x / Math.pow(10, exp);
    var nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
    return nf * Math.pow(10, exp);
  }

  function renderLineChart(sim){
    var VW = 760, VH = 340, ML = 62, MR = 14, MT = 18, MB = 30;
    var PW = VW - ML - MR, PH = VH - MT - MB;
    var n = sim.years + 1;
    var maxVal = Math.max.apply(null, sim.opt);
    var niceMax = niceCeil(maxVal * 1.08);

    function xAt(i){ return ML + (i / (n - 1)) * PW; }
    function yAt(v){ return MT + PH - (v / niceMax) * PH; }

    while (lineSvg.firstChild) lineSvg.removeChild(lineSvg.firstChild);

    // gridlines + y labels
    var ticks = [0, 0.25, 0.5, 0.75, 1];
    ticks.forEach(function(t){
      var yy = MT + PH - t * PH;
      lineSvg.appendChild(svgEl("line", { x1:ML, x2:VW-MR, y1:yy, y2:yy, stroke:"var(--border)", "stroke-width":1 }));
      var lbl = svgEl("text", { x:ML-8, y:yy+4, "text-anchor":"end", style:"font:500 11px var(--font-mono);fill:var(--text-muted)" });
      lbl.textContent = fmtCompact(niceMax * t);
      lineSvg.appendChild(lbl);
    });

    // x labels
    var step = n <= 11 ? 1 : Math.ceil((n-1) / 8);
    for (var i = 0; i < n; i += step){
      var lx = svgEl("text", { x:xAt(i), y:VH-8, "text-anchor":"middle", style:"font:500 11px var(--font-mono);fill:var(--text-muted)" });
      lx.textContent = i;
      lineSvg.appendChild(lx);
    }
    if ((n-1) % step !== 0){
      var lxEnd = svgEl("text", { x:xAt(n-1), y:VH-8, "text-anchor":"middle", style:"font:500 11px var(--font-mono);fill:var(--text-muted)" });
      lxEnd.textContent = n-1;
      lineSvg.appendChild(lxEnd);
    }
    var axisCaption = svgEl("text", { x:(ML+VW-MR)/2, y:VH-2, "text-anchor":"middle", style:"font:500 10px var(--font-sans);fill:var(--text-muted)" });
    axisCaption.textContent = "Anos";
    lineSvg.appendChild(axisCaption);

    // band path
    var bandPts = [];
    for (i = 0; i < n; i++) bandPts.push(xAt(i) + "," + yAt(sim.opt[i]));
    for (i = n-1; i >= 0; i--) bandPts.push(xAt(i) + "," + yAt(sim.pes[i]));
    lineSvg.appendChild(svgEl("polygon", { points: bandPts.join(" "), fill:"var(--accent)", opacity:"0.12", stroke:"none" }));

    function linePath(arr){
      var d = "";
      for (var j = 0; j < n; j++){ d += (j===0?"M":"L") + xAt(j) + "," + yAt(arr[j]) + " "; }
      return d;
    }
    lineSvg.appendChild(svgEl("path", { d: linePath(sim.opt), fill:"none", stroke:"var(--text-muted)", "stroke-width":1.4, "stroke-linecap":"round", "stroke-linejoin":"round" }));
    lineSvg.appendChild(svgEl("path", { d: linePath(sim.pes), fill:"none", stroke:"var(--text-muted)", "stroke-width":1.4, "stroke-linecap":"round", "stroke-linejoin":"round" }));
    lineSvg.appendChild(svgEl("path", { d: linePath(sim.base), fill:"none", stroke:"var(--accent)", "stroke-width":2.6, "stroke-linecap":"round", "stroke-linejoin":"round" }));

    // end marker + label
    var endX = xAt(n-1), endY = yAt(sim.base[n-1]);
    lineSvg.appendChild(svgEl("circle", { cx:endX, cy:endY, r:5, fill:"var(--accent)", stroke:"var(--surface)", "stroke-width":2.5 }));
    var labelText = fmtUSD(sim.base[n-1]);
    var labelW = 14 + labelText.length * 6.6;
    var labelX = Math.min(endX - labelW/2, VW - MR - labelW);
    labelX = Math.max(labelX, ML);
    var labelY = Math.max(endY - 30, MT + 4);
    lineSvg.appendChild(svgEl("rect", { x:labelX, y:labelY, width:labelW, height:22, rx:6, fill:"var(--accent)" }));
    var labelT = svgEl("text", { x:labelX + labelW/2, y:labelY + 15, "text-anchor":"middle", style:"font:600 12px var(--font-mono);fill:var(--accent-ink)" });
    labelT.textContent = labelText;
    lineSvg.appendChild(labelT);

    // hover layer
    var hoverGroup = svgEl("g", { style:"pointer-events:none" });
    var crossLine = svgEl("line", { x1:0, x2:0, y1:MT, y2:MT+PH, stroke:"var(--border-strong)", "stroke-width":1, opacity:"0" });
    var dotBase = svgEl("circle", { r:4.5, fill:"var(--accent)", stroke:"var(--surface)", "stroke-width":2, opacity:"0" });
    var dotOpt = svgEl("circle", { r:3.5, fill:"var(--text-muted)", stroke:"var(--surface)", "stroke-width":2, opacity:"0" });
    var dotPes = svgEl("circle", { r:3.5, fill:"var(--text-muted)", stroke:"var(--surface)", "stroke-width":2, opacity:"0" });
    hoverGroup.appendChild(crossLine); hoverGroup.appendChild(dotOpt); hoverGroup.appendChild(dotPes); hoverGroup.appendChild(dotBase);
    lineSvg.appendChild(hoverGroup);

    var hitRect = svgEl("rect", { x:ML, y:MT, width:PW, height:PH, fill:"transparent" });
    lineSvg.appendChild(hitRect);

    function showTooltip(clientEvt, idx){
      var rect = lineSvg.getBoundingClientRect();
      var scale = rect.width / VW;
      var px = xAt(idx) * scale, py = yAt(sim.base[idx]) * scale;
      crossLine.setAttribute("x1", xAt(idx)); crossLine.setAttribute("x2", xAt(idx)); crossLine.setAttribute("opacity", "1");
      dotBase.setAttribute("cx", xAt(idx)); dotBase.setAttribute("cy", yAt(sim.base[idx])); dotBase.setAttribute("opacity", "1");
      dotOpt.setAttribute("cx", xAt(idx)); dotOpt.setAttribute("cy", yAt(sim.opt[idx])); dotOpt.setAttribute("opacity", "1");
      dotPes.setAttribute("cx", xAt(idx)); dotPes.setAttribute("cy", yAt(sim.pes[idx])); dotPes.setAttribute("opacity", "1");

      lineTooltip.hidden = false;
      lineTooltip.innerHTML = "";
      var title = document.createElement("div"); title.className = "tooltip-title"; title.textContent = "Ano " + idx;
      lineTooltip.appendChild(title);
      [["Optimista", sim.opt[idx], "var(--text-muted)"], ["Base", sim.base[idx], "var(--accent)"], ["Pesimista", sim.pes[idx], "var(--text-muted)"]].forEach(function(row){
        var r = document.createElement("div"); r.className = "tooltip-row";
        var k = document.createElement("span"); k.className = "k";
        var key = document.createElement("span"); key.style.cssText = "width:10px;height:2px;background:" + row[2] + ";display:inline-block;border-radius:2px";
        var kt = document.createElement("span"); kt.textContent = row[0];
        k.appendChild(key); k.appendChild(kt);
        var v = document.createElement("span"); v.className = "v"; v.textContent = fmtUSD(row[1]);
        r.appendChild(k); r.appendChild(v);
        lineTooltip.appendChild(r);
      });
      var left = Math.min(Math.max(px - 80, 0), rect.width - 170);
      var top = Math.max(py - 96, 0);
      lineTooltip.style.left = left + "px";
      lineTooltip.style.top = top + "px";
    }
    function hideTooltip(){
      crossLine.setAttribute("opacity", "0"); dotBase.setAttribute("opacity", "0");
      dotOpt.setAttribute("opacity", "0"); dotPes.setAttribute("opacity", "0");
      lineTooltip.hidden = true;
    }
    hitRect.addEventListener("pointermove", function(evt){
      var rect = lineSvg.getBoundingClientRect();
      var scale = VW / rect.width;
      var xInVb = (evt.clientX - rect.left) * scale;
      var idx = Math.round(((xInVb - ML) / PW) * (n-1));
      idx = Math.max(0, Math.min(n-1, idx));
      showTooltip(evt, idx);
    });
    hitRect.addEventListener("pointerleave", hideTooltip);
  }

  // ---------- rendering: donut ----------
  var donutSvg = document.getElementById("donutChart");
  var donutTooltip = document.getElementById("donutTooltip");
  var donutLegendEl = document.getElementById("donutLegend");
  var donutCenterValueEl = document.getElementById("donutCenterValue");

  function renderDonut(sim){
    while (donutSvg.firstChild) donutSvg.removeChild(donutSvg.firstChild);
    donutLegendEl.innerHTML = "";

    var cx = 110, cy = 110, r = 78, sw = 34;
    var circumference = 2 * Math.PI * r;
    var cum = 0;
    var assets = normalizedAssets();

    donutSvg.appendChild(svgEl("circle", { cx:cx, cy:cy, r:r, fill:"none", stroke:"var(--border)", "stroke-width":sw }));

    assets.forEach(function(a){
      var frac = a.weight / 100;
      var len = frac * circumference;
      var gap = frac > 0.003 ? 2.4 : 0;
      var visLen = Math.max(len - gap, 0.0001);
      var circle = svgEl("circle", {
        cx:cx, cy:cy, r:r, fill:"none", "stroke-width":sw,
        "stroke-dasharray": visLen + " " + (circumference - visLen),
        "stroke-dashoffset": -(cum + gap/2),
        transform: "rotate(-90 " + cx + " " + cy + ")",
        style: "stroke:var(" + a.colorVar + ");cursor:pointer;transition:stroke-width .12s"
      });
      circle.setAttribute("tabindex", "0");
      circle.setAttribute("aria-label", a.name + ", " + fmtPctPlain(a.weight,1));
      circle.addEventListener("pointerenter", function(evt){ showDonutTooltip(evt, a); circle.setAttribute("stroke-width", sw+4); });
      circle.addEventListener("pointermove", function(evt){ positionDonutTooltip(evt); });
      circle.addEventListener("pointerleave", function(){ hideDonutTooltip(); circle.setAttribute("stroke-width", sw); });
      circle.addEventListener("focus", function(evt){ showDonutTooltip(evt, a); });
      circle.addEventListener("blur", hideDonutTooltip);
      donutSvg.appendChild(circle);
      cum += len;

      var li = document.createElement("li");
      var dot = document.createElement("span"); dot.style.cssText = "width:10px;height:10px;border-radius:3px;background:var(" + a.colorVar + ")";
      var name = document.createElement("span"); name.className = "name"; name.textContent = a.name;
      var pct = document.createElement("span"); pct.className = "pct"; pct.textContent = fmtPctPlain(a.weight, 1);
      li.appendChild(dot); li.appendChild(name); li.appendChild(pct);
      donutLegendEl.appendChild(li);
    });

    donutCenterValueEl.textContent = fmtCompact(sim.capital);

    function showDonutTooltip(evt, a){
      donutTooltip.hidden = false;
      donutTooltip.innerHTML = "";
      var title = document.createElement("div"); title.className = "tooltip-title"; title.textContent = a.name;
      donutTooltip.appendChild(title);
      var r1 = document.createElement("div"); r1.className = "tooltip-row";
      var k1 = document.createElement("span"); k1.className = "k"; k1.textContent = "Peso";
      var v1 = document.createElement("span"); v1.className = "v"; v1.textContent = fmtPctPlain(a.weight, 1);
      r1.appendChild(k1); r1.appendChild(v1); donutTooltip.appendChild(r1);
      var r2 = document.createElement("div"); r2.className = "tooltip-row";
      var k2 = document.createElement("span"); k2.className = "k"; k2.textContent = "Rent. esperada";
      var v2 = document.createElement("span"); v2.className = "v"; v2.textContent = fmtPctPlain(a.ret, 1);
      r2.appendChild(k2); r2.appendChild(v2); donutTooltip.appendChild(r2);
      positionDonutTooltip(evt);
    }
    function positionDonutTooltip(evt){
      var wrapRect = donutSvg.parentElement.getBoundingClientRect();
      var x = (evt.clientX !== undefined ? evt.clientX : wrapRect.left + wrapRect.width/2) - wrapRect.left;
      var y = (evt.clientY !== undefined ? evt.clientY : wrapRect.top) - wrapRect.top;
      donutTooltip.style.left = Math.min(Math.max(x - 70, -4), wrapRect.width - 140) + "px";
      donutTooltip.style.top = Math.max(y - 78, -4) + "px";
    }
    function hideDonutTooltip(){ donutTooltip.hidden = true; }
  }

  // ---------- rendering: table ----------
  var tableBodyEl = document.getElementById("assetTableBody");
  var tableHorizonNoteEl = document.getElementById("tableHorizonNote");
  function renderTable(sim){
    tableBodyEl.innerHTML = "";
    tableHorizonNoteEl.textContent = "A " + sim.years + (sim.years===1?" ano":" anos") + (state.inflacionOn ? ", en valor real" : ", en valor nominal");
    sim.perAsset.forEach(function(p){
      var a = p.asset;
      var tr = document.createElement("tr");

      var tdName = document.createElement("td");
      var cell = document.createElement("div"); cell.className = "asset-cell";
      var dot = document.createElement("span"); dot.className = "dot"; dot.style.background = "var(" + a.colorVar + ")";
      var nm = document.createElement("span"); nm.className = "nm"; nm.textContent = a.name;
      cell.appendChild(dot); cell.appendChild(nm); tdName.appendChild(cell);

      var tdWeight = document.createElement("td"); tdWeight.textContent = fmtPctPlain(a.weight, 1);
      var tdRet = document.createElement("td"); tdRet.textContent = fmtPctPlain(a.ret, 1);
      var tdInit = document.createElement("td"); tdInit.textContent = fmtUSD0(p.initA);
      var tdFinal = document.createElement("td"); tdFinal.textContent = fmtUSD0(p.finalBase);
      var tdShare = document.createElement("td");
      tdShare.textContent = sim.valorFinal > 0 ? fmtPctPlain((p.finalBase / sim.valorFinal) * 100, 1) : "0,0%";

      tr.appendChild(tdName); tr.appendChild(tdWeight); tr.appendChild(tdRet);
      tr.appendChild(tdInit); tr.appendChild(tdFinal); tr.appendChild(tdShare);
      tableBodyEl.appendChild(tr);
    });
  }

  // ---------- orchestration ----------
  function onStateChange(){
    saveState();
    render();
  }
  function render(){
    var sum = weightSum();
    var offTarget = Math.abs(sum - 100) > 0.15;
    weightTotalEl.textContent = "Total " + fmtPctPlain(sum, 1);
    weightTotalEl.classList.toggle("off", offTarget);

    var sim = runSimulation();
    renderKPIs(sim);
    renderLineChart(sim);
    renderDonut(sim);
    renderTable(sim);
  }

  renderTopControls();
  renderAssetControls();
  render();

  window.addEventListener("resize", function(){ /* SVGs are viewBox-responsive; no reflow work needed */ });
}
